import {User} from "../../../../../api/accessControl/User.ts";
import {useState, useMemo, useEffect, useReducer} from "react";
import {
    DndContext,
    DragOverlay as DndDragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {MatchableCard} from "./components/MatchableCard.tsx";
import {DragOverlay} from "./components/DragOverlay.tsx";
import {DroppableList} from "./components/DroppableList.tsx";
import {useDragAndDrop} from "./hooks/useDragAndDrop.ts";
import {PartialMatchGroupCard} from "./components/PartialMatchGroupCard.tsx";
import {
    Transaction,
    Document,
    AttachedItem,
    LIST_IDS,
    DEFAULT_CURRENCY,
    MatchAcceptData,
} from "./dndTypes.ts";
import {
    filterDocuments, filterTransactions, getDocumentRelationName,
    sortTransactions, sortDocuments,
    convertDocumentToAttachedItem, convertTransactionToAttachedItem,
} from "./utils/utils.ts";
import {
    useDocumentDrawer,
    useOpenDocument,
} from "../../../../../views/DocumentDrawer";
import {Client} from "../../Client.ts";
import SearchSlideInput from "../../../../../components/general/SearchSlideInput.tsx";
import { RenderTypeEnum, TransactionMatchPart, BillMatchPart, BillDocument, isBillDocument } from "./types.ts";
import { dndReducer, initialState } from "./dndReducer.ts";
import type {DocumentDirection} from "../../../../../generated/enums.ts";

interface DndMatchPageProps {
    currentUser: User;
    initialTransactions: Transaction[];
    initialDocuments: Document[];
    onAccept: (transactionIds: string[], billIds: string[]) => void | Promise<void>;
    onAddPartsToExistingMatch: (matchId: string, transactionIds: string[], billIds: string[]) => Promise<void>;
    client?: Client | null;
    onBookTransactionAsPrivate?: (transactionId: string) => Promise<void>;
    onCreateBlacklistRule?: (transaction: Transaction, client: Client | null) => void;
    onWrongGLAClick?: (transaction: Transaction) => void;
    onCreditCardSettlement?: (transaction: Transaction, businessId: string | null) => void;
    onMarkBillAsPrivatelyPaid?: (documentId: string) => Promise<void>;
    onUnmatch: (billIds: string[], matchId: string,  transactionIds: string[]) => Promise<void>;
    onAddParts: (matchId: string, transactionIds: string[], billIds: string[]) => Promise<void>;
    onMarkAsComplete?: (matchId: string, defaultDate: string) => void;
    showMatched?: boolean;
}

export default function DndMatchPage({
                                         currentUser,
                                         initialTransactions,
                                         initialDocuments,
                                         onAccept,
                                         onAddPartsToExistingMatch,
                                         client,
                                         onBookTransactionAsPrivate,
                                         onCreateBlacklistRule,
                                         onWrongGLAClick,
                                         onCreditCardSettlement,
                                         onMarkBillAsPrivatelyPaid,
                                         onUnmatch,
                                         onAddParts,
                                         onMarkAsComplete,
                                         showMatched = false,
                                     }: DndMatchPageProps) {
    const canProcessCreditCardStatements = currentUser.flags.getFlag('PARTNER__CAN_PROCESS_CREDIT_CARD_STATEMENTS');
    const canBookVatOnTransaction = currentUser.flags.getFlag('PARTNER__CAN_BOOK_VAT_ON_TRANSACTION');

    const [state, dispatch] = useReducer(
        dndReducer,
        { transactions: initialTransactions, documents: initialDocuments },
        (init) => initialState(init.transactions, init.documents)
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleAttachDocument = (hostId: string, documentId: string) => {
        dispatch({
            type: 'ATTACH_ITEM',
            payload: { kind: 'document', document_id: documentId, host_id: hostId },
        });
    };

    const handleAttachTransaction = (hostId: string, transactionId: string) => {
        dispatch({
            type: 'ATTACH_ITEM',
            payload: { kind: 'transaction', transaction_id: transactionId, host_id: hostId },
        });
    };

    const handleDetach = (hostId: string, guestId: string) => {
        dispatch({
            type: 'DETACH_ITEM',
            payload: { hostId, guestId },
        });
    };

    const handleAcceptTransaction = async (
        data: MatchAcceptData
    ) => {
        const {transactionIds, billIds, targetMatchId} = data;
        dispatch({
            type: 'CLEAR_TEMPORARY_ATTACHMENTS',
            payload: { hostId: data.currentItemId },
        });
        if (targetMatchId) {
            await onAddPartsToExistingMatch(targetMatchId, transactionIds, billIds);
        } else {
            await onAccept(transactionIds, billIds);
        }
    };

    const handleRejectTransaction = (transactionId: string) => {
        dispatch({
            type: 'CLEAR_TEMPORARY_ATTACHMENTS',
            payload: { hostId: transactionId },
        });
    };

    const handleAcceptDocument = async (
        data: MatchAcceptData
    ) => {
        const {currentItemId, billIds, transactionIds, targetMatchId} = data;
        dispatch({
            type: 'CLEAR_TEMPORARY_ATTACHMENTS',
            payload: { hostId: currentItemId },
        });
        if (targetMatchId) {
            await onAddPartsToExistingMatch(targetMatchId, transactionIds, billIds);
        } else {
            await onAccept(transactionIds, billIds);
        }
    };

    const handleRejectDocument = (documentId: string) => {
        dispatch({
            type: 'CLEAR_TEMPORARY_ATTACHMENTS',
            payload: { hostId: documentId },
        });
    };

    const handleManualAddParts = async (
        matchId: string,
        transactionIds: string[],
        billIds: string[],
        currentItemId: string
    ) => {
        dispatch({
            type: 'CLEAR_TEMPORARY_ATTACHMENTS',
            payload: { hostId: currentItemId },
        });
        await onAddParts(matchId, transactionIds, billIds);
    };

    const attachedDocumentIds = useMemo(() =>
        new Set(state.temporaryAttachments.filter(a => a.kind === 'document').map(a => a.document_id)),
        [state.temporaryAttachments]
    );

    const attachedTransactionIds = useMemo(() =>
        new Set(state.temporaryAttachments.filter(a => a.kind === 'transaction').map(a => a.transaction_id)),
        [state.temporaryAttachments]
    );

    const visibleDocuments = useMemo(() =>
        state.documents.filter(d => isBillDocument(d) ? !attachedDocumentIds.has(d.document_id) : true),
        [state.documents, attachedDocumentIds]
    );

    const visibleTransactions = useMemo(() =>
        state.transactions.filter(t => !attachedTransactionIds.has(t.transaction_id)),
        [state.transactions, attachedTransactionIds]
    );

    const attachmentsByHost = useMemo(() => {
        const map: Record<string, AttachedItem[]> = {};
        for (const attachment of state.temporaryAttachments) {
            if (attachment.kind === 'document') {
                const doc = state.documents.find(d => isBillDocument(d) && d.document_id === attachment.document_id);
                if (!doc || !isBillDocument(doc)) continue;
                (map[attachment.host_id] ??= []).push(convertDocumentToAttachedItem(doc));
            } else {
                const tx = state.transactions.find(t => t.transaction_id === attachment.transaction_id);
                if (!tx) continue;
                (map[attachment.host_id] ??= []).push(convertTransactionToAttachedItem(tx));
            }
        }
        return map;
    }, [state.temporaryAttachments, state.documents, state.transactions]);

    const {handleDragStart, handleDragEnd, collisionDetection} = useDragAndDrop({
        transactions: visibleTransactions,
        documents: visibleDocuments,
        temporaryAttachments: state.temporaryAttachments,
        onAttachDocument: handleAttachDocument,
        onAttachTransaction: handleAttachTransaction,
        onDetach: handleDetach,
        setActiveId: (id) => dispatch({ type: 'SET_ACTIVE_DRAG', payload: id }),
    });

    useEffect(() => {
        dispatch({
            type: 'SYNC_BACKEND_DATA',
            payload: {
                transactions: initialTransactions,
                documents: initialDocuments,
            },
        });
    }, [initialTransactions, initialDocuments]);

    const documentsForDrawer = useMemo(() => {
        const attachedDocs = state.temporaryAttachments
            .filter(a => a.kind === 'document')
            .map(a => state.documents.find(d => isBillDocument(d) && d.document_id === a.document_id))
            .filter((d): d is BillDocument => !!d && isBillDocument(d));
        const combined = [...visibleDocuments.filter(isBillDocument), ...attachedDocs];
        const unique = new Map<string, BillDocument>();
        combined.forEach((d) => {
            unique.set(d.document_id, d);
        });
        return Array.from(unique.values());
    }, [visibleDocuments, state.temporaryAttachments, state.documents]);

    const documentDrawer = useDocumentDrawer();
    const openDocument = useOpenDocument();

    const drawerDocuments = useMemo(
        () => documentsForDrawer.map((d) => ({id: d.document_id, businessId: d.business_id, fileName: d.file_name})),
        [documentsForDrawer]
    );

    const handleOpenDocument = (docId: string) => {
        const doc = documentsForDrawer.find((d) => d.document_id === docId);
        if (doc) {
            documentDrawer.open(doc.business_id, doc.document_id, {initialTab: 0, documentList: drawerDocuments});
        }
    };

    const handleOpenBillDocument = (businessId: string, documentId: string) => {
        documentDrawer.open(businessId, documentId, {initialTab: 0});
    };

    const handleMatchClick = (docId: string) => {
        const doc = documentsForDrawer.find((d) => d.document_id === docId);
        if (doc) {
            documentDrawer.open(doc.business_id, doc.document_id, {initialTab: 1, documentList: drawerDocuments});
        }
    };

    const handleMarkBillAsPrivatelyPaid = async (documentId: string) => {
        if (!onMarkBillAsPrivatelyPaid) {
            return;
        }
        await onMarkBillAsPrivatelyPaid(documentId);
        if (openDocument?.documentId === documentId) {
            documentDrawer.close();
        }
    };

    const activeTransaction = useMemo(
        () => state.transactions.find((t) => t.transaction_id === state.activeDragId) || null,
        [state.transactions, state.activeDragId]
    );

    const activeDocument = useMemo<BillDocument | null>(
        () => state.documents.find((d): d is BillDocument => isBillDocument(d) && d.document_id === state.activeDragId) || null,
        [state.documents, state.activeDragId]
    );

    const activeAttachedItem = useMemo(() => {
        if (!state.activeDragId) return null;

        const isAttachedDoc = state.temporaryAttachments.some(
            a => a.kind === 'document' && a.document_id === state.activeDragId
        );
        const attachedDoc = isAttachedDoc
            ? state.documents.find((d): d is BillDocument => isBillDocument(d) && d.document_id === state.activeDragId)
            : undefined;
        if (attachedDoc && attachedDoc.scanned_fields) {
            const relationName = getDocumentRelationName(attachedDoc.document_direction, attachedDoc.scanned_fields);
                return {
                relation_name: relationName,
                amount: attachedDoc.scanned_fields.total_amount_payable,
                metadata: `${attachedDoc.scanned_fields.reference_identifier || "onbekend"} ${attachedDoc.file_name}`,
                date: attachedDoc.scanned_fields.normalized_datetime,
                currency: attachedDoc.scanned_fields.total_amount_payable.currency || DEFAULT_CURRENCY,
                document_direction: attachedDoc.document_direction,
                };
            }

        const isAttachedTx = state.temporaryAttachments.some(
            a => a.kind === 'transaction' && a.transaction_id === state.activeDragId
        );
        const attachedTx = isAttachedTx
            ? state.transactions.find((t) => t.transaction_id === state.activeDragId)
            : undefined;
        if (attachedTx) {
                return {
                relation_name: attachedTx.relation_name,
                amount: attachedTx.total_amount_payable,
                date: attachedTx.booking_date,
                currency: attachedTx.bank_account_currency || DEFAULT_CURRENCY,
                };
        }

        return null;
    }, [state.activeDragId, state.temporaryAttachments, state.documents, state.transactions]);

    const sortedTransactions = useMemo(
        () => sortTransactions(visibleTransactions, attachmentsByHost),
        [visibleTransactions, attachmentsByHost]
    );

    const sortedDocuments = useMemo(
        () => sortDocuments(visibleDocuments, attachmentsByHost),
        [visibleDocuments, attachmentsByHost]
    );

    const [transactionsSearchQuery, setTransactionsSearchQuery] = useState("");
    const [documentsSearchQuery, setDocumentsSearchQuery] = useState("");

    const displayedTransactions = useMemo(() => {
        return filterTransactions(sortedTransactions, transactionsSearchQuery)
    }, [sortedTransactions, transactionsSearchQuery]);

    const displayedDocuments = useMemo(() => {
        return filterDocuments(sortedDocuments, documentsSearchQuery)
    }, [sortedDocuments, documentsSearchQuery]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => dispatch({ type: 'SET_ACTIVE_DRAG', payload: null })}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                {/* Transactions List */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-700">Transacties ({displayedTransactions.length})</h3>
                        <SearchSlideInput value={transactionsSearchQuery} onChange={setTransactionsSearchQuery} />
                    </div>

                    <DroppableList
                        id={LIST_IDS.TRANSACTIONS}
                        className="p-3 border border-gray-200 rounded-md bg-gray-100 h-[calc(100vh-16rem)] overflow-y-auto"
                    >
                        {displayedTransactions.map((transaction) => {
                            const isTransactionMatched = transaction.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
                            const attachedItems = attachmentsByHost[transaction.transaction_id] || [];

                            return (
                                <div key={transaction.transaction_id}>
                                    <MatchableCard
                                        canProcessCreditCardStatements={canProcessCreditCardStatements}
                                        canBookVatOnTransaction={canBookVatOnTransaction}
                                        id={transaction.transaction_id}
                                        isMatched={isTransactionMatched}
                                        matchId={isTransactionMatched ? (transaction as TransactionMatchPart).match_id : undefined}
                                        isDragging={state.activeDragId === transaction.transaction_id}
                                        attachedItems={attachedItems}
                                        onRemoveItem={(itemId) =>
                                            handleDetach(
                                                transaction.transaction_id,
                                                itemId
                                            )
                                        }
                                        onAccept={handleAcceptTransaction}
                                        onReject={() =>
                                            handleRejectTransaction(
                                                transaction.transaction_id
                                            )
                                        }
                                        activeDraggingId={state.activeDragId}
                                        transaction={transaction}
                                        activeTransaction={activeTransaction}
                                        activeDocument={activeDocument}
                                        onAttachedDocumentClick={(docId) => handleOpenDocument(docId)}
                                        client={client}
                                        onBookTransactionAsPrivate={onBookTransactionAsPrivate}
                                        onCreateBlacklistRule={onCreateBlacklistRule}
                                        onWrongGLAClick={onWrongGLAClick}
                                        onCreditCardSettlement={onCreditCardSettlement}
                                        onMatchClick={handleMatchClick}
                                        showMatched={showMatched}
                                    />
                                </div>
                            );
                        })}
                        {displayedTransactions.length === 0 && (
                            <div className="text-center text-gray-500 py-8">
                                Geen openstaande transacties gevonden
                            </div>
                        )}
                    </DroppableList>
                </div>

                {/* Documents List */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-700">Documenten ({displayedDocuments.length})</h3>
                        <SearchSlideInput value={documentsSearchQuery} onChange={setDocumentsSearchQuery} />
                    </div>
                    <DroppableList
                        id={LIST_IDS.DOCUMENTS}
                        className="p-3 border border-gray-200 rounded-md bg-gray-100 h-[calc(100vh-16rem)] overflow-y-auto"
                    >
                        {displayedDocuments.map((document) => {
                            if (document.render_type === RenderTypeEnum.PARTIAL_MATCH) {
                                const attachedItems = attachmentsByHost[document.match_id] || [];
                                return (
                                    <PartialMatchGroupCard
                                        key={document.match_id}
                                        partialMatch={document}
                                        attachedItems={attachedItems}
                                        onRemoveItem={(itemId) =>
                                            handleDetach(document.match_id, itemId)
                                        }
                                        onAddParts={(mId, transactionIds, billIds) =>
                                            handleManualAddParts(mId, transactionIds, billIds, document.match_id)
                                        }
                                        onReject={() =>
                                            handleRejectDocument(document.match_id)
                                        }
                                        activeDraggingId={state.activeDragId}
                                        activeTransaction={activeTransaction}
                                        activeDocument={activeDocument}
                                        onAttachedDocumentClick={(docId) => handleOpenDocument(docId)}
                                        onOpenBillDocument={handleOpenBillDocument}
                                        onUnmatch={onUnmatch}
                                        onMarkAsComplete={onMarkAsComplete}
                                    />
                                );
                            }

                            const isDocMatched = document.render_type === RenderTypeEnum.BILL_MATCH_PART;
                            const attachedItems = attachmentsByHost[document.document_id] || [];

                            return (
                                <MatchableCard
                                    key={document.document_id}
                                    id={document.document_id}
                                    isMatched={isDocMatched}
                                    matchId={isDocMatched ? (document as BillMatchPart).match_id : undefined}
                                    billId={document.bill_id}
                                    isDragging={state.activeDragId === document.document_id}
                                    attachedItems={attachedItems}
                                    onRemoveItem={(itemId) =>
                                        handleDetach(
                                            document.document_id,
                                            itemId
                                        )
                                    }
                                    onAccept={handleAcceptDocument}
                                    onReject={() =>
                                        handleRejectDocument(document.document_id)
                                    }
                                    activeDraggingId={state.activeDragId}
                                    document={document}
                                    activeTransaction={activeTransaction}
                                    activeDocument={activeDocument}
                                    onDescriptionClick={() => handleOpenDocument(document.document_id)}
                                    onMatchClick={handleMatchClick}
                                    onMarkBillAsPrivatelyPaid={handleMarkBillAsPrivatelyPaid}
                                    showMatched={showMatched}
                                />
                            );
                        })}
                        {displayedDocuments.length === 0 && (
                            <div className="text-center text-gray-500 py-8">
                                Geen documenten gevonden
                            </div>
                        )}
                    </DroppableList>
                </div>
            </div>

            <DndDragOverlay>
                <DragOverlay
                    activeTransaction={
                        activeTransaction
                            ? {
                                relation_name: activeTransaction.relation_name,
                                desc: activeTransaction.desc,
                                total_amount_payable:
                                activeTransaction.total_amount_payable,
                                currency: activeTransaction.bank_account_currency,
                            }
                            : null
                    }
                    activeDocument={
                        activeDocument && activeDocument.scanned_fields
                            ? {
                                relation_name: activeDocument.document_direction === 'purchase_invoice'
                                    ? activeDocument.scanned_fields.seller_name
                                    : activeDocument.scanned_fields.buyer_name ?? "N/A",
                                file_name: activeDocument.file_name,
                                total_amount_payable:
                                activeDocument.scanned_fields.total_amount_payable,
                                currency:
                                    activeDocument.scanned_fields.total_amount_payable.currency ||
                                    DEFAULT_CURRENCY,
                                document_direction: activeDocument.document_direction,
                            }
                            : null
                    }
                    activeAttachedItem={activeAttachedItem}
                />
            </DndDragOverlay>
        </DndContext>
    );
}
